import {ApiError, CheckoutFactory, CountriesFactory, ICartPosition, ICountriesLookup} from "@connox/typescript-library";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import {useEffect, useState} from "react";
import {Gateway, GatewayDest} from "react-gateway";
import {useTranslation} from "react-i18next";
import {FaLock} from "react-icons/fa";
import MediaQuery, {useMediaQuery} from "react-responsive";
import Checkout, {DEFAULT_ERROR_MESSAGE} from "../../checkout/Checkout";
import CheckoutContextProvider from "../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import CheckoutViewContextProvider from "../../checkout/CheckoutContextProvider/CheckoutViewContextProvider";
import AdvantagesBanner from "../../lib/Banner/AdvantagesBanner";
import {PaymentMethodsBanner} from "../../lib/Banner/PaymentMethodsBanner";
import {Button, ButtonStyles, ButtonType} from "../../lib/form/Button/Button";
import {getShopId} from "../../lib/getShopId";
import AmazonPayButtonReact from "../../lib/payments/AmazonPayButtonReact";
import {cancelAmazonPay, isAmazonLoggedIn} from "../../lib/payments/AmazonPayLibrary";
import {PayPalExpressButton} from "../../lib/payments/PayPalExpressButton";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import CartDetails from "../CartDetails/CartDetails";
import ItemList from "../ItemList/ItemList";
import PromotionalItems from "../ItemList/PromotionalItems";
import VoucherBlock from "../Rebates/VoucherBlock";
import {ShippingCostBar} from "../ShippingCostBar/ShippingCostBar";
import EmptyCart from "./EmptyCart";
import {ICartMessage} from "@connox/typescript-library/types/cart/CartMessage";
import styles from "./ShoppingCart.module.scss";
import "../../lib/SvgCart.scss";
import "../../lib/ArrowBorder.scss";
import {updateUrlParams} from "../CartIcon/CartIconPortal";

export const TotallyEmptyCart: React.FC = () => {
  return (
    <div className={styles.wrapper}>
      <MediaQuery query="not all and (min-width: 768px)">
        <EmptyCart />
        <VoucherBlock />
      </MediaQuery>
      <MediaQuery query="(min-width: 768px)">
        <div className={styles.list}>
          <EmptyCart />
        </div>
        <div className={styles.details}>
          <VoucherBlock />
        </div>
      </MediaQuery>
    </div>
  );
};

const ShoppingCart: React.FC = () => {
  const {cart, isIntoCheckout, setIsCartVisible, setIsIntoCheckout} = useCartContext();
  const {t} = useTranslation("cart");
  const [errors, setErrors] = useState<string[]>([]);
  const itemPositions = cart.positions.filter((pos: ICartPosition) => pos.reference && pos.reference.itemId !== null);

  const invalidPositions = cart.positions.filter((pos) => !!pos.messages.find((msg) => msg.type === "error"));
  if (!!invalidPositions.length) {
    Sentry.setExtra("positions_with_error_messages", {
      deliveryCountry: cart.countryCode,
      errorPositions: invalidPositions.map((pos) => pos.messages.map((msg) => msg.code).join(" ")),
    });
  }

  useEffect(() => {
    setErrors([]);
  }, [isIntoCheckout]);

  let destinationButtonName = "ButtonMobile";
  let destinationName = "CartErrorMobile";
  // This MediaQuery is necessary because of a bug in GatewayDest
  // in combination with <MediaQuery> component, showing double
  // error entries in ul in mobile mode (second MediaQuery Comp
  // renders invisible, still outputting Cart Error Tag)
  if (useMediaQuery({query: "(min-width: 768px)"})) {
    destinationButtonName = "ButtonDesktop";
    destinationName = "CartErrorDesktop";
  }

  const ButtonGroup: React.FC = (buttonGatewayProps) => {
    if (React.Children.toArray(buttonGatewayProps.children).length) {
      return <ToCheckoutButton cartHasErrors={true} />;
    }

    if (isAmazonLoggedIn()) {
      return (
        <React.Fragment>
          <AmazonPayButtonReact onReturn={checkoutButtonReturn} handleError={expressCheckoutError} />
          <ToCheckoutButton cartHasErrors={false} />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <ToCheckoutButton cartHasErrors={false} />
        <AmazonPayButtonReact onReturn={checkoutButtonReturn} handleError={expressCheckoutError} />
        <PayPalExpressButton onReturn={checkoutButtonReturn} handleError={expressCheckoutError} />
      </React.Fragment>
    );
  };

  const buttons = React.useMemo(() => {
    return <GatewayDest component={ButtonGroup} name={destinationButtonName} />;
  }, []);

  if (!itemPositions.length) {
    return <TotallyEmptyCart />;
  }

  const checkoutButtonReturn = () => {
    setErrors([]);
    setIsIntoCheckout(true);
    updateUrlParams("checkout");
  };

  const expressCheckoutError = (expressError: ApiError | PossibleNetworkError) => {
    const tempError = ((expressError as ApiError).originalError || expressError) as PossibleNetworkError;

    if (__DEVELOPMENT__) {
      // eslint-disable-next-line no-console
      console.error(tempError);
    }

    let errorArray: string[] = [];
    const {response} = tempError;
    // if response is defined, it's a network error
    if (response) {
      if (response.data.messages?.length) {
        errorArray = response.data.messages.map((msg: ICartMessage) => msg.code);
      } else if (response.data.result) {
        errorArray = [response.data.result.code || response.data.result];
      } else if (response.data.detail) {
        errorArray = [response.data.detail];
      }
    } else if (tempError.message) {
      errorArray = tempError.message.includes("\u000A") ? tempError.message.split("\u000A") : [tempError.message];
    }
    if (!errorArray.length) {
      errorArray.push(DEFAULT_ERROR_MESSAGE);
    }

    const section = document.querySelector("#ModalRootElement > section");
    if (section) {
      if (typeof section.scrollTo === "function") {
        section.scrollTo({left: 0, top: 0, behavior: "auto"});
      } else {
        section.scrollTop = 0;
        section.scrollLeft = 0;
      }
    }

    const consent = document.cookie.split("CookieConsent=")[1];
    const sentryEventId = Sentry.captureException(tempError, {
      extra: {
        place: "express checkout return error in cart view",
        error: tempError,
        cartLogisticsType: cart.logisticsTypeId,
        cookieConsent: consent?.split(";")[0],
      },
    });
    errorArray = errorArray.map((err) => t(err, t(DEFAULT_ERROR_MESSAGE, {ns: "checkout", sentryEventId})));
    setErrors(errorArray);
  };

  const ToCheckoutButton: React.FC<{cartHasErrors: boolean}> = (checkoutButtonProps) => {
    if (isAmazonLoggedIn()) {
      const logoutFunction = async () => {
        const tempCheckout = await CheckoutFactory.generateCheckout(getShopId(), cart);
        cancelAmazonPay();
        await tempCheckout.put({paymentId: null});
        setErrors([]);
        /* just triggering re-render */
        setIsCartVisible(true);
      };

      return (
        <span className={styles.amazonPayLogoutLink} onClick={logoutFunction}>
          {t("Zahlung mit Amazon Pay abbrechen", {ns: "checkout"})}
        </span>
      );
    }

    return (
      <Button
        action={checkoutButtonReturn}
        styles={[ButtonStyles.wide]}
        type={checkoutButtonProps.cartHasErrors ? ButtonType.disabled : ButtonType.default}
      >
        <FaLock color={"lightgreen"} />
        {t("Zur Kasse")}
      </Button>
    );
  };

  const RedBorderBox: React.FC = (cartErrorProps) => {
    const listEntries = React.Children.map(cartErrorProps.children, (child: string, index: number) => (
      <li key={`li_${index}`}>{child}</li>
    ));
    if (!(listEntries && listEntries.length)) {
      return null;
    }

    return (
      <div className="error-block-main">
        <ul>{listEntries}</ul>
      </div>
    );
  };

  /* Will be 'executed' by being placed in react dom component structure */
  const ConnectionError: React.FC = () => {
    if (!errors.length) {
      return null;
    }
    const gatewayChildren = errors.map((error, index) => <React.Fragment key={index}>{error}</React.Fragment>);

    return <Gateway into={destinationName}>{gatewayChildren}</Gateway>;
  };

  if (isIntoCheckout) {
    return (
      <CheckoutContextProvider>
        <CheckoutViewContextProvider>
          <Checkout />
        </CheckoutViewContextProvider>
      </CheckoutContextProvider>
    );
  }

  /* Will be 'executed' by being placed in react dom component structure */
  const CartError: React.FC = () => {
    const [country, setCountry] = useState<string | undefined>();
    let isMounted = true;

    useEffect(() => {
      void CountriesFactory.getAllCountries(getShopId()).then(() => {
        const countryLookup = CountriesFactory.getCountryByCode(cart.countryCode);
        if (isMounted) {
          setCountry(countryLookup?.name);
        }
      });

      return () => {
        isMounted = false;
      };
    }, [cart.countryCode]);

    if (!invalidPositions.length && !cart.messages.length && cart.isValid) {
      return null;
    }

    // reducing to translated error strings out of positions[].messages[]
    let errorStrings = invalidPositions.reduce<string[]>((accu, position) => {
      const voucher = position.reference?.voucher ?? null;
      const minimumOrderValue = voucher?.minimumOrderValueAbsolute?.formatted;
      const minimumQuantity = voucher?.minimumOrderQuantityAbsolute;
      const voucherCode = voucher?.code || position.reference?.voucherCode || "";

      const msgs = position.messages.map((msg) => {
        return t(msg.code, {country, minimumOrderValue, minimumQuantity, voucherCode});
      });

      if (msgs.length) {
        accu = accu.concat(msgs);
      }

      return accu;
    }, []);

    // reducing to translated error strings out of rebates[].messages[]
    errorStrings = errorStrings.concat(
      cart.rebates.reduce<string[]>((accumulator, current) => {
        const minimumOrderValue = current.minimumOrderValueAbsolute?.formatted ?? "";
        const voucherCode = current.code;
        const msgs = current.messages.map((msg) => t(msg.code, {minimumOrderValue, voucherCode}));

        return msgs.length ? accumulator.concat(msgs) : accumulator;
      }, []),
    );

    // reducing to translated error strings out of cart.messages[]
    errorStrings = errorStrings.concat(
      cart.messages.reduce<string[]>((accumulator, current) => {
        // message code 'NO_SHIPPING_SELECTED' already covered by message
        // code 'SHIPPING_NOT_FOUND' in position of type 'Shipping'
        if (current.code !== "NO_SHIPPING_SELECTED") accumulator.push(t(current.code, {country}));

        return accumulator;
      }, []),
    );

    return (
      <React.Fragment>
        {/* this does the red border box magic. See the .map call on */}
        {/* children props followed by length check in RedBorderBox */}
        <Gateway into={destinationName}>{errorStrings}</Gateway>

        {/* this 'only' does the disabled 'To checkout' button magic. See */}
        {/* `React.Children.toArray(buttonGatewayProps.children).length` in ButtonGroup */}
        <Gateway into={destinationButtonName}>{errorStrings}</Gateway>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.wrapper}>
      <MediaQuery query="not all and (min-width: 768px)">
        <ShippingCostBar />
        <h2>{t("Ihr Warenkorb", {count: itemPositions.length})}</h2>
        <GatewayDest component={RedBorderBox} name={destinationName} />
        {/* Following two components are randomly placed here */}
        <ConnectionError />
        <CartError />
        <PaymentMethodsBanner />
        <AdvantagesBanner />
        <ItemList />
        <PromotionalItems />
        <VoucherBlock />
        <CountrySelector />
        <CartDetails />
        {buttons}
      </MediaQuery>
      <MediaQuery query="(min-width: 768px)">
        <div className={styles.list}>
          <h2>{t("Ihr Warenkorb", {count: itemPositions.length})}</h2>
          <GatewayDest component={RedBorderBox} name={destinationName} />
          {/* Following two components are randomly placed here */}
          <ConnectionError />
          <CartError />
          <ItemList />
          <PromotionalItems />
          <AdvantagesBanner />
        </div>
        <div className={styles.details}>
          <div className={styles.block}>
            <VoucherBlock />
            <CartDetails />
            <ShippingCostBar />
            <CountrySelector />
            <PaymentMethodsBanner />
            {buttons}
          </div>
        </div>
      </MediaQuery>
    </div>
  );
};

const CountrySelector = () => {
  const [countries, setCountries] = React.useState<ICountriesLookup[]>([]);
  const {cart} = useCartContext();
  const {t} = useTranslation("cart");
  const select = React.useRef<HTMLSelectElement>(null);
  const onSelect = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    e.persist();
    const countryCode = e.currentTarget.value;

    if (select.current) {
      const optionElement = select.current.querySelector(`[value=${countryCode}]`);

      if (optionElement) {
        optionElement.innerHTML = `${t("Versand nach: {{country}}", {country: optionElement.innerHTML})}`;
      }
    }

    cart.put({countryCode}).catch(Sentry.captureException);
  };

  React.useEffect(() => {
    CountriesFactory.getAllCountries(getShopId())
      .then((result) => {
        setCountries(result.filter((country) => country.isDeliveryAllowed));
        if (select.current) {
          select.current.value = cart.countryCode;
        }
      })
      .catch(Sentry.captureException);
  }, []);

  if (countries.length === 1) {
    return (
      <div style={{marginBottom: "1rem", padding: "0.5em 1em", border: "1px solid #f1f2f3"}}>
        {`${t("Versand nach: {{country}}", {country: countries[0].name})}`}
      </div>
    );
  } else {
    return (
      <select ref={select} onChange={onSelect} style={{textIndent: ".5rem", width: "100%", marginBottom: ".5rem"}}>
        {countries.length ? <CountryOptions countries={countries} /> : <option>{t("Versand nach:")}</option>}
      </select>
    );
  }
};

const CountryOptions: React.FC<{countries: ICountriesLookup[]}> = (props) => {
  const {cart} = useCartContext();
  const {t} = useTranslation("cart");

  return (
    <React.Fragment>
      {props.countries.map((country) => {
        return (
          <option key={country.countryCode} value={country.countryCode}>
            {cart.countryCode === country.countryCode
              ? `${t("Versand nach: {{country}}", {country: country.name})} `
              : country.name}
          </option>
        );
      })}
    </React.Fragment>
  );
};

export default ShoppingCart;

export interface PossibleNetworkError extends Error {
  response?: {
    data: {
      messages?: ICartMessage[];
      result?: string & {code: string};
      detail?: string;
      status?: number;
      title?: string;
      type?: string;
    };
    status?: number;
  };
}
